import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { RowNode } from "ag-grid-community";
import SnResizeBar from "@shared/components/SnResizeBar";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_SAL_SAO_REG_002_POP_001 from "./SN_SAL_SAO_REG_002_POP_001";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 수주관리
const SN_SAL_SAO_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sal_sao_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onSave = async (msg?: string) => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const salSohChanges = gridRefs.current["grid1"].current.getChange();
      const salSolChanges = gridRefs.current["grid2"].current.getChange();

      if (
        salSohChanges.modifiedRowcount === 0 &&
        salSolChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        sal_soh: salSohChanges,
        sal_sol: salSolChanges,
      };

      const ok = await fnSave("SN_SAL_SAO_REG_002_SAVE_01", saveData, msg);
      if (!ok) return false;

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH) {
        const result = await callApi("SN_SAL_SAO_REG_002_SAVE_01_getStSo", {
          cd_firm: userInfo.cd_firm,
          no_so: focusedNodeH.data.no_so,
        });

        const status = result.status;
        const data = result.data;
        if (!status) return false;

        const st_so = data.st_so;
        if (st_so) focusedNodeH.setDataValue("st_so", st_so, "noChange");
      }

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    const rowCount = gridRefs.current["grid1"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    let isUpdated = false;
    let msg = "";
    try {
      if (id == "btnConfirmH") {
        const confirm = await showDialog("confirm", "승인처리 하시겠습니까?");
        if (!confirm) return;
        msg = "APPROVE";

        const focusedNodeH =
          await gridRefs.current["grid1"].current.focusedRowNode();
        if (!focusedNodeH) return;

        let displayedNodes: any = [];
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            displayedNodes.push(node);
          }
        );
        for (const node of displayedNodes) {
          if (!node?.data) continue;
          if (
            node.data.no_so === focusedNodeH.data?.no_so &&
            node.data.st_so === "F"
          ) {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_so",
              "R"
            );
            isUpdated = true;
          }
        }

        if (!isUpdated) {
          await showDialog("error", "처리실패. 미승인건이 없습니다.");
          return;
        }
      }
      if (id == "btnCancelH") {
        const confirm = await showDialog("confirm", "승인취소 하시겠습니까?");
        if (!confirm) return;

        msg = "APPROVE_CANCEL";

        const focusedNodeH =
          await gridRefs.current["grid1"].current.focusedRowNode();
        if (!focusedNodeH) return;

        if (focusedNodeH.data?.yn_app === "Y") {
          await showDialog(
            "alter",
            "수주형태 '자동승인' 건은 취소할 수 없습니다."
          );
          return;
        }

        let displayedNodes: any = [];
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            displayedNodes.push(node);
          }
        );

        for (const node of displayedNodes) {
          if (!node?.data) continue;
          if (
            node.data.no_so === focusedNodeH.data?.no_so &&
            node.data.st_so === "R"
          ) {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_so",
              "F"
            );
            isUpdated = true;
          }
        }
        if (!isUpdated) {
          await showDialog("error", "처리실패. 승인건이 없습니다.");
          return;
        }
      }
      await onSave(msg);
    } catch (error) {}
  };

  const onGrid2BtnsClick = async (id: string) => {
    const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    const selectedDataL = gridRefs.current["grid2"].current.getSelectedRows();
    if (selectedDataL.length === 0) {
      await showDialog(
        "error",
        "선택된 내용이 없습니다. 항목을 선택해 주세요."
      );
      return;
    }

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    let displayedNodes: any[] = [];
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      displayedNodes.push(node);
    });

    const selectedNodes = displayedNodes.filter(
      (node: any) => node?.data && node.isSelected()
    );

    let msg = "";
    try {
      if (id === "btnConfirmL") {
        const confirm = await showDialog("confirm", "승인처리 하시겠습니까?");
        if (!confirm) return;

        msg = "APPROVE";

        let isUpdated = false;
        for (const node of selectedNodes) {
          if (!node?.data) continue;

          const st_so = String(node.data.st_so ?? "");
          switch (st_so) {
            case "P":
              await showDialog("alter", "확정된 건만 승인 가능합니다.");
              return;
            case "R":
              await showDialog("alter", "이미 승인 처리된 건입니다.");
              return;
            case "S":
              await showDialog("alter", "승인 처리 후 진행중인 건 입니다.");
              return;
            case "C":
              await showDialog("alter", "마감된 건 입니다.");
              return;
            case "X":
              await showDialog("alter", "전표 취소된 건 입니다.");
              return;
          }

          if (node.data.st_so === "F") {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_so",
              "R"
            );
            isUpdated = true;
          }
        }
        if (!isUpdated) return;
      }
      if (id == "btnCancelL") {
        if (focusedNodeH.data?.yn_app === "Y") {
          await showDialog(
            "alter",
            "수주형태 '자동승인' 건은 취소할 수 없습니다."
          );
          return;
        }

        const confirm = await showDialog("confirm", "승인취소 하시겠습니까?");
        if (!confirm) return;

        msg = "APPROVE_CANCEL";

        let isUpdated = false;
        for (const node of selectedNodes) {
          if (!node?.data) continue;

          const st_so = String(node.data.st_so ?? "");
          switch (st_so) {
            case "P":
            case "F":
              await showDialog("alter", "승인되지 않은 건 입니다.");
              return;
            case "S":
              await showDialog(
                "alter",
                "진행중인 건은 승인 취소할 수 없습니다."
              );
              return;
            case "C":
              await showDialog("alter", "마감된 건은 승인 취소할 수 없습니다.");
              return;
            case "X":
              await showDialog("alter", "이미 전표 취소된 건 입니다.");
              return;
          }

          if (node.data.st_so === "R" && node.data.qt_req === 0) {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_so",
              "F"
            );
            isUpdated = true;
          }
        }
        if (!isUpdated) return;
      }
      if (id === "btnCloseL") {
        const confirm = await showDialog("confirm", "마감처리 하시겠습니까?");
        if (!confirm) return;

        msg = "CLOSE";

        for (const node of selectedNodes) {
          const dc_reason_close = String(node.data.dc_reason_close ?? "");
          const st_so = String(node.data.st_so ?? "");
          if (st_so !== "C" && dc_reason_close === "") {
            await showDialog("alter", "종결사유를 입력하시기 바랍니다.");
            gridRefs.current["grid2"].current.setFocusedCell(
              node.rowIndex,
              "dc_reason_close"
            );
            return;
          }
        }

        const allStSo = selectedNodes.map((node: any) =>
          String(node.data.st_so ?? "")
        );
        const hasC = allStSo.includes("C");
        if (hasC) {
          await showDialog("alter", "이미 마감처리된 건이 존재합니다.");
          return;
        }

        let isUpdated = false;
        for (const node of selectedNodes) {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_so",
            "C"
          );
          isUpdated = true;
        }
        if (!isUpdated) return;
      }
      if (id === "btnCloseCancelL") {
        msg = "CLOSE_CANCEL";
        const yn_app = focusedNodeH?.data?.yn_app ?? "";

        const confirm = await showDialog("confirm", "마감취소 하시겠습니까?");
        if (!confirm) return;

        const allStSo = selectedNodes.map((node: any) =>
          String(node.data.st_so ?? "")
        );
        const hasC = allStSo.includes("C");
        if (!hasC) {
          await showDialog("alter", "마감처리 되지 않는 건이 존재합니다.");
          return;
        }

        for (const node of selectedNodes) {
          const st_so = String(node.data.st_so ?? "");
          const qt_req = Number(node.data.qt_req ?? 0);
          if (st_so === "C") {
            if (qt_req > 0) {
              gridRefs.current["grid2"].current.setValueImmediately(
                node,
                "st_so",
                "S"
              );
            } else if (qt_req === 0) {
              if (yn_app === "Y") {
                gridRefs.current["grid2"].current.setValueImmediately(
                  node,
                  "st_so",
                  "R"
                );
              } else {
                gridRefs.current["grid2"].current.setValueImmediately(
                  node,
                  "st_so",
                  "F"
                );
              }
            }
          } else {
            await showDialog("alter", "마감처리 되지 않는 건입니다.");
            return;
          }
        }
      }
      await onSave(msg);
    } catch (error) {}
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_so = selectedRow.no_so;
      const dt_so = selectedRow.dt_so || "";
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setSelectedDocu(no_so);
      setSelectedDtDocu(dt_so);
    }
  };

  const handleContextMenu = async (event: any) => {
    const id = event.id;
    const menu = event.menu;

    switch (id) {
      case "devideQt":
        await devideQt();
        break;
      case "applyClose":
        await applyClose();
        break;
      case "cancelClose":
        await cancelClose();
        break;
    }
  };

  const devideQt = async () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (focusedNodeL.data.qt - focusedNodeL.data.qt_req === 0) {
      await showDialog("alter", "분할 대상수량이 없습니다.");
      return;
    }
    if (focusedNodeL.data.st_so === "C") {
      await showDialog("alter", "이미 마감처리된 건입니다.");
      return;
    }

    const selectedData = {
      ...focusedNodeL?.data,
      fg_trns: focusedNodeH.data.fg_trns,
      rt_exch: focusedNodeH.data.rt_exch,
      cd_exch: focusedNodeH.data.cd_exch,
    };
    const maxNoLine =
      gridRefs.current["grid2"]?.current?.getMaxValue("no_line");
    const component = (
      <SN_SAL_SAO_REG_002_POP_001
        selectedData={selectedData}
        maxNoLine={maxNoLine}
      />
    );
    const result = await showResizablePopupModal({
      title: "잔량분할",
      children: component,
      width: "1070px",
      height: "570px",
      validation: true,
    });
    if (!result || result === "close") return;

    const insertList = result[0].insert;
    for (const i of insertList) {
      gridRefs.current["grid2"].current.addRow(i);
    }

    const updateList = result[0].update;
    // const updateRow = updateList[0];
    // Object.keys(updateRow).forEach((key) => {
    //   focusedNodeL.setDataValue(key, updateRow[key], "noEvent");
    // });
    focusedNodeL.setData({
      ...focusedNodeL.data,
      ...updateList[0],
    });

    gridRefs.current["grid1"].current.resetUpdate();
    gridRefs.current["grid2"].current.resetUpdate();
  };

  const applyClose = async () => {
    const focusedRowNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (focusedRowNodeL.data.st_so === "C") {
      await showDialog("alter", "이미 마감처리된 건입니다.");
      return;
    }

    gridRefs.current["grid2"].current.setValueImmediately(
      focusedRowNodeL,
      "st_so",
      "C"
    );
    return await onSave("CLOSE");
  };

  const cancelClose = async () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const yn_app = focusedNodeH?.data?.yn_app ?? "";

    const focusedRowNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (focusedRowNodeL.data.st_so !== "C") {
      await showDialog("alter", "마감처리 되지 않는 건입니다.");
      return;
    }

    const qt_req = Number(focusedRowNodeL.data.qt_req ?? 0);
    if (qt_req > 0) {
      gridRefs.current["grid2"].current.setValueImmediately(
        focusedRowNodeL,
        "st_so",
        "S"
      );
    } else if (qt_req === 0) {
      if (yn_app === "Y") {
        gridRefs.current["grid2"].current.setValueImmediately(
          focusedRowNodeL,
          "st_so",
          "R"
        );
      } else {
        gridRefs.current["grid2"].current.setValueImmediately(
          focusedRowNodeL,
          "st_so",
          "F"
        );
      }
    }
    return await onSave("CLOSE_CANCEL");
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onContextMenu: handleContextMenu,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "수주정보" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"수주별승인처리"}
                  onClick={() => {
                    onGrid1BtnsClick("btnConfirmH");
                  }}
                />
                <SnButton
                  text={"수주별승인취소"}
                  onClick={() => {
                    onGrid1BtnsClick("btnCancelH");
                  }}
                />
              </div>
            }
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_so"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
            header={
              <div>
                <SnButton
                  text={"승인처리"}
                  onClick={() => {
                    onGrid2BtnsClick("btnConfirmL");
                  }}
                />
                <SnButton
                  text={"승인취소"}
                  onClick={() => {
                    onGrid2BtnsClick("btnCancelL");
                  }}
                />
                <SnButton
                  text={"마감처리"}
                  onClick={() => {
                    onGrid2BtnsClick("btnCloseL");
                  }}
                />
                <SnButton
                  text={"마감취소"}
                  onClick={() => {
                    onGrid2BtnsClick("btnCloseCancelL");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
});

export default SN_SAL_SAO_REG_002;
